/**
 * Utilitaires pour les commandes
 */

export type PaymentStatus = 'paid' | 'unpaid' | 'pending';

export type OrderStatusCategory = 'delivered' | 'processing' | 'pending' | 'cancelled';

type OrderWithPayment = {
  payment_status?: string | null;
  paymentStatus?: string | null;
};

/**
 * Normalise le statut de paiement (snake_case API, casse variable).
 */
export function getPaymentStatus(order: OrderWithPayment): PaymentStatus | null {
  const raw = order.payment_status ?? order.paymentStatus;
  if (!raw) return null;
  const normalized = String(raw).toLowerCase();
  if (normalized === 'paid' || normalized === 'unpaid' || normalized === 'pending') {
    return normalized;
  }
  return null;
}

export function isOrderPaid(order: OrderWithPayment): boolean {
  return getPaymentStatus(order) === 'paid';
}

/**
 * Regroupe les statuts B2C (anglais) et B2B (français) pour les rapports.
 */
export function getOrderStatusCategory(status: string | undefined | null): OrderStatusCategory | null {
  if (!status) return null;
  switch (status) {
    case 'delivered':
    case 'livree':
      return 'delivered';
    case 'processing':
    case 'shipped':
    case 'validee':
    case 'facture_emise':
    case 'paiement_differe':
      return 'processing';
    case 'pending':
    case 'devis':
      return 'pending';
    case 'cancelled':
    case 'annulee':
      return 'cancelled';
    default:
      return null;
  }
}

/**
 * Parse les items d'une commande de manière sécurisée
 * Gère les cas où items est déjà un tableau, une chaîne JSON, ou null/undefined
 */
export function parseOrderItems(items: any): any[] {
  if (!items) return [];
  
  // Si c'est déjà un tableau, le retourner tel quel
  if (Array.isArray(items)) return items;
  
  // Si c'est un objet mais pas un tableau, retourner un tableau vide
  if (typeof items === 'object' && !Array.isArray(items)) {
    console.warn('parseOrderItems: items est un objet non-tableau, retour d\'un tableau vide', items);
    return [];
  }
  
  // Si c'est une chaîne, essayer de la parser
  if (typeof items === 'string') {
    try {
      const trimmed = items.trim();
      // Vérifier que c'est bien du JSON valide (commence par [ ou {)
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        const parsed = JSON.parse(items);
        // S'assurer que le résultat est un tableau
        return Array.isArray(parsed) ? parsed : [];
      }
      // Si ce n'est pas du JSON valide, retourner un tableau vide
      return [];
    } catch (error) {
      console.error('Erreur lors du parsing des items de commande:', error, 'Items:', items);
      return [];
    }
  }
  
  // Cas par défaut: retourner un tableau vide
  return [];
}
